import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { SheetField } from '../settings/sheet';
import { DetailPanel, GHOST_BTN, INPUT_CLS, Notice, PRIMARY_BTN, SELECT_CLS } from '../shared/listPage';
import {
  ACTIVE_STATUSES, LOST_REASON_OPTIONS, Lead, LeadInput, LeadStatus, NEEDS_NEXT, STATUS_COLORS, STATUS_OPTIONS, kindLabel,
  leadValue, relativeDay, stageProblem, statusLabel,
} from './salesModel';

/**
 * Πωλήσεις → «Στάδια» (απόφαση χρήστη 3/11/2026): μία στήλη ανά στάδιο, κάρτες που σέρνονται από στήλη σε στήλη.
 * Η μετακίνηση γράφεται αμέσως όταν δεν χρειάζεται τίποτα άλλο· αλλιώς ρωτά μόνο ό,τι λείπει (ίδιοι κανόνες με την
 * κάρτα και τον server): «Χάθηκε» → λόγος, «Αργότερα» → πότε, επαφή…διαπραγμάτευση χωρίς ημερομηνία → επόμενο βήμα.
 * Κλικ σε κάρτα = η κάρτα του υποψηφίου (εκεί αλλάζει το στάδιο και από πληκτρολόγιο / οθόνη αφής).
 */

const COLUMNS: LeadStatus[] = ['new', 'contacted', 'meeting', 'proposal', 'negotiation', 'won', 'later', 'lost'];

/** Χρειάζεται ερώτηση πριν από τη μετακίνηση; */
export function moveNeedsInput(lead: Lead, to: LeadStatus): boolean {
  if (to === 'lost' || to === 'later') return true;
  return NEEDS_NEXT.includes(to) && !lead.next_action_at;
}

interface BoardProps {
  leads: Lead[];
  today: string;
  onOpen: (id: number) => void;
  onMove: (lead: Lead, to: LeadStatus) => void;
}

export const SalesBoard: React.FC<BoardProps> = ({ leads, today, onOpen, onMove }) => {
  const [over, setOver] = useState<LeadStatus | null>(null);
  const byStatus = (s: LeadStatus) => leads.filter(l => l.status === s)
    .sort((a, b) => (a.next_action_at || '9999').localeCompare(b.next_action_at || '9999') || (b.fit_score ?? 0) - (a.fit_score ?? 0));

  return (
    <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1" data-testid="sales-board">
      {COLUMNS.map(status => {
        const list = byStatus(status);
        const value = list.reduce((t, l) => t + leadValue(l), 0);
        return (
          <section
            key={status}
            aria-label={statusLabel(status)}
            data-testid={`sales-col-${status}`}
            onDragOver={e => { e.preventDefault(); setOver(status); }}
            onDragLeave={() => setOver(cur => (cur === status ? null : cur))}
            onDrop={e => {
              e.preventDefault();
              setOver(null);
              const id = Number(e.dataTransfer.getData('text/plain'));
              const lead = leads.find(l => l.id === id);
              if (lead && lead.status !== status) onMove(lead, status);
            }}
            className={`w-64 shrink-0 rounded-xl border bg-surface-2 flex flex-col max-h-[75vh] transition-colors ${
              over === status ? 'border-brand ring-2 ring-brand/30' : 'border-line'}`}
          >
            <header className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-line">
              <span className="flex items-center gap-2 text-sm font-semibold text-t1">
                <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[status] }} />
                {statusLabel(status)}
              </span>
              <span className="text-xs text-t3 tabular-nums text-right" data-testid={`sales-col-${status}-count`}>
                {list.length}
                {value > 0 && <span className="block">{Math.round(value).toLocaleString('el-GR')} €/μ</span>}
              </span>
            </header>
            <ul className="flex-1 overflow-y-auto p-2 space-y-2">
              {list.map(l => {
                const late = ACTIVE_STATUSES.includes(l.status) && l.next_action_at && l.next_action_at < today;
                return (
                  <li key={l.id}>
                    <button
                      type="button"
                      draggable
                      onDragStart={e => { e.dataTransfer.setData('text/plain', String(l.id)); e.dataTransfer.effectAllowed = 'move'; }}
                      onClick={() => onOpen(l.id)}
                      className="w-full text-left rounded-lg border border-line bg-surface-1 px-3 py-2 hover:border-line-strong hover:bg-surface-0 cursor-grab active:cursor-grabbing"
                    >
                      <span className="block text-sm font-medium text-t1 truncate">{l.name}</span>
                      <span className="block text-xs text-t3 truncate">{[kindLabel(l.kind), l.area].filter(Boolean).join(' · ')}</span>
                      {(ACTIVE_STATUSES.includes(l.status) && l.next_action_at) || l.fit_score ? (
                        <span className="mt-1 flex items-center justify-between gap-2 text-xs">
                          <span className={late ? 'text-red-700 dark:text-red-400 font-medium' : 'text-t2'}>
                            {ACTIVE_STATUSES.includes(l.status) && l.next_action_at ? relativeDay(l.next_action_at, today) : ''}
                          </span>
                          {l.fit_score ? <span className="text-t3 tabular-nums">βαθμός {l.fit_score}</span> : null}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

interface MoveProps {
  /** Η μετακίνηση που περιμένει απάντηση (null = κλειστό). */
  move: { lead: Lead; to: LeadStatus } | null;
  today: string;
  onCancel: () => void;
  onConfirm: (lead: Lead, changes: LeadInput) => Promise<void>;
}

/** Ό,τι λείπει για μια μετακίνηση στις στήλες — όχι ολόκληρη η κάρτα. */
export const MoveDialog: React.FC<MoveProps> = ({ move, today, onCancel, onConfirm }) => {
  const [v, setV] = useState<LeadInput>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!move) return;
    setError(null);
    setV({ next_action: move.lead.next_action, next_action_at: move.lead.next_action_at || today, lost_reason: null, lost_note: null, retry_at: null });
  }, [move, today]);

  if (!move) return <DetailPanel open={false} title="" onClose={onCancel}>{null}</DetailPanel>;
  const { lead, to } = move;
  const label = STATUS_OPTIONS.find(o => o.value === to)?.label || to;
  const changes: LeadInput = to === 'lost'
    ? { status: to, lost_reason: v.lost_reason, lost_note: v.lost_note, retry_at: v.retry_at }
    : to === 'later'
      ? { status: to, retry_at: v.retry_at }
      : { status: to, next_action: v.next_action, next_action_at: v.next_action_at };

  const save = async () => {
    const problem = stageProblem({ name: lead.name, ...changes });
    if (problem) { setError(problem); return; }
    setBusy(true);
    try {
      await onConfirm(lead, changes);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Η μετακίνηση απέτυχε');
    } finally {
      setBusy(false);
    }
  };
  const field = (k: keyof LeadInput, title: string, type = 'text') => (
    <SheetField label={title} htmlFor={`mv-${k}`}>
      <input id={`mv-${k}`} type={type} className={INPUT_CLS} value={(v[k] as string | null) ?? ''}
        onChange={e => setV({ ...v, [k]: e.target.value || null })} />
    </SheetField>
  );

  return (
    <DetailPanel
      open
      onClose={onCancel}
      title={`${lead.name} → ${label}`}
      testId="sales-move-dialog"
      footer={(
        <>
          <button type="button" className={GHOST_BTN} onClick={onCancel}>Άκυρο</button>
          <button type="button" className={PRIMARY_BTN} onClick={save} disabled={busy} data-testid="sales-move-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />} Μετακίνηση
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        {error && <Notice tone="danger" testId="sales-move-error">{error}</Notice>}
        {to === 'lost' && (
          <>
            <SheetField label="Γιατί χάθηκε" htmlFor="mv-lost_reason">
              <select id="mv-lost_reason" className={SELECT_CLS} value={v.lost_reason ?? ''}
                onChange={e => setV({ ...v, lost_reason: e.target.value || null })}>
                <option value="">—</option>
                {LOST_REASON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </SheetField>
            {field('lost_note', 'Λεπτομέρειες')}
            {field('retry_at', 'Ξαναδοκίμασε στις (προαιρετικό)', 'date')}
          </>
        )}
        {to === 'later' && field('retry_at', 'Ξαναδοκίμασε στις', 'date')}
        {to !== 'lost' && to !== 'later' && (
          <>
            {field('next_action', 'Επόμενο βήμα: τι')}
            {field('next_action_at', 'Πότε', 'date')}
          </>
        )}
      </div>
    </DetailPanel>
  );
};
